import { useCallback } from 'react';

import useStores from 'shared/hooks/use-stores';

import { ModalNames, useModals } from 'components/modals';
import { editBankAccount, relinkBankAccount } from 'src/payments/constants';
import useDutchiePayEnrollment from 'src/hooks/use-dutchiePay-enrollment';
import { useCheckout } from 'src/checkout/context';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { WhiteLabelConfig } from 'src/payments/hooks';
import { BankDetailResponse, getBankDetails } from 'src/payments/utils/bank-utils';
import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import {
  DispensaryPaymentOptions,
  getUnenrolledAlertMessage,
} from 'src/checkout/components/sections/payment/shared/utils';
import { usePaymentSelectionContext } from 'src/checkout/components/sections/payment/shared/context';

export type UsePayByBankDetailsResponse = {
  bankDetails: BankDetailResponse | null;
  bankNeedsRelinking: boolean;
  onEditBank: () => void;
  onRelinkBank: () => void;
  showBankInformation: boolean;
  loggedOutPBBSignupEnabled: boolean;
  loggedInPBBSignupEnabled: boolean;
  hasOtherOrderTypesWithPBBEnabled: boolean;
  unenrolledAlertMessage: string;
  handleEmbeddedDutchiePayLink: () => void;
  insideTabContainer: boolean;
  showSaveButton: boolean;
  onlinePaymentOption: DispensaryPaymentOptions[number] | null;
  hasOnlineAndOfflinePaymentOptions: boolean;
  whiteLabelConfig: WhiteLabelConfig;
  saveOnlinePaymentMethod: () => void;
  openConnectingBankModal: () => void;
  closeConnectingBankModal: () => void;
};

export const usePayByBankDetails = (): UsePayByBankDetailsResponse => {
  // Hooks
  const { openModal, closeModal } = useModals();
  const { Cart, UI } = useStores();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { setExpanded, expanded } = usePaymentSelectionContext();
  const {
    bankInformation,
    showBankInformation,
    hasOtherOrderTypesWithPBBEnabled,
    loggedInPBBSignupEnabled,
    loggedOutPBBSignupEnabled,
    offlinePaymentOptionValues,
    hasOnlineAndOfflinePaymentOptions,
    paymentMethodIsDutchiePay,
    onlinePaymentOptionIsDutchiePay,
    isEnrolledDutchiePay,
    onlinePaymentOption,
    whiteLabelConfig,
    needsRelinkBankAccount: bankNeedsRelinking,
  } = useCartPayment();

  const Checkout = useCheckout();

  const { openDutchiePay } = useOpenDutchiePay({
    cname: Cart.dispensary.cName,
    onCloseModal: () => DutchiePayEnrollment.setAutoCheckoutAfterBankLinkReconnect(false),
  });
  const bankDetails = bankInformation ? getBankDetails(bankInformation) : null;
  // Conditional Rendering
  const unenrolledAlertMessage = getUnenrolledAlertMessage(offlinePaymentOptionValues.length > 0);
  const checkoutPaymentError = Checkout.getError({ card: 'payment' });
  const finalMessage =
    checkoutPaymentError.error && checkoutPaymentError.message === unenrolledAlertMessage ? unenrolledAlertMessage : '';

  const insideTabContainer = expanded && hasOnlineAndOfflinePaymentOptions;
  const showSaveButton =
    paymentMethodIsDutchiePay && onlinePaymentOptionIsDutchiePay && isEnrolledDutchiePay && !bankNeedsRelinking;
  // Handlers
  const openTermsModal = useCallback(() => openModal(ModalNames.termsOfService), [openModal]);
  const openConnectingBankModal = useCallback(() => void openModal(ModalNames.connectingBank), [openModal]);
  const closeConnectingBankModal = useCallback(() => closeModal(ModalNames.connectingBank, {}), [closeModal]);

  const onEditBank = (): void => {
    if (UI?.isMobileEcommApp) {
      void openModal(ModalNames.dutchiePayVisitOnDesktop);
    } else {
      DutchiePayEnrollment.editBankAccount();
      openDutchiePay(editBankAccount);
    }
  };

  const onRelinkBank = (): void => {
    if (UI?.isMobileEcommApp) {
      void openModal(ModalNames.dutchiePayRelinkBankAccount);
    } else {
      DutchiePayEnrollment.reconnectBankAccount();
      openDutchiePay(relinkBankAccount);
    }
  };

  const handleEmbeddedDutchiePayLink = useCallback(() => {
    Checkout.removeError({ card: 'payment' });
    closeConnectingBankModal();
    void openTermsModal();
  }, [Checkout, openTermsModal, closeConnectingBankModal]);

  const saveOnlinePaymentMethod = (): void => {
    setExpanded(false);
    Checkout.removeError({ match: 'payment' });
  };

  return {
    bankDetails,
    bankNeedsRelinking,
    showBankInformation,
    loggedOutPBBSignupEnabled,
    loggedInPBBSignupEnabled,
    hasOtherOrderTypesWithPBBEnabled,
    unenrolledAlertMessage: finalMessage,
    onEditBank,
    onRelinkBank,
    handleEmbeddedDutchiePayLink,
    insideTabContainer,
    showSaveButton,
    onlinePaymentOption,
    hasOnlineAndOfflinePaymentOptions,
    whiteLabelConfig,
    saveOnlinePaymentMethod,
    openConnectingBankModal,
    closeConnectingBankModal,
  };
};
